import { type FC, useState, useId, useRef } from "react";
import { useDoc, usePouch } from "use-pouchdb";
import { useParams, useSearchParams } from "react-router";
import * as uuid from "uuid";

import { normalizeItemInfo, attrNameRegex, type ItemInfo } from "../../common/schema";
import { computeShelcode } from "../../common/shelcode";
import { assert, isPouchError, sortUniqueStrings } from "../utils";
import { useScanner } from "../Scanner";
import { InputRow, AttrRow } from "../form_inputs";
import { ItemById } from "../ItemItem";
import { ItemPicker, TemplatePicker } from "../pickers";
import { ItemTemplateLink, ItemTemplateInheritedAttrs, ItemTemplateNotes, ItemTemplateChildren } from "../templates";
import { ItemChildren, TagEditor, useAttrData, ScancodeEditor, ItemIconSection, ItemFriends } from "../item_sections";
import { ItemAttachments } from "../ItemAttachments";

export const Item:FC = () => {
  const { id } = useParams<{id: string}>();
  assert(id != null);
  const db = usePouch();
  const docInfo = useDoc<Partial<ItemInfo>>(id);
  const [searchParams] = useSearchParams();
  const [newAttrNames, setNewAttrNames] = useState<Set<string>>(new Set());
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showMovePicker, setShowMovePicker] = useState(false);
  const attrsTbodyId = useId();
  const newAttrFormId = useId();
  const mainFormId = useId();
  const attrNameSuggestionsId = useId();
  const attrData = useAttrData();
  const [focusAttrName, setFocusAttrName] = useState<string|null>(null);
  const shelcodeInputRef = useRef<HTMLInputElement>(null);
  const { startScanner: startShelcodeScanner } = useScanner({
    onScanResult: (result) => {
      if (result == null) return;
      const el = shelcodeInputRef.current;
      if (el == null) return;
      el.value = result.text;
      el.dispatchEvent(new Event("input", { bubbles: true }));
    },
  });
  const [shelcodeAssignError, setShelcodeAssignError] = useState<string | null>(null);
  const assignShelcode = async () => {
    setShelcodeAssignError(null);
    try {
      const result = await db.query("built/shelcode_available", { limit: 1, include_docs: true });
      const row = result.rows[0];
      if (row == null) {
        setShelcodeAssignError("No available shelcodes");
        return;
      }
      assert(row.doc != null);
      const code = computeShelcode(row.doc._id);
      // Save both atomically: mark the shelcode doc as used, then save the item.
      await db.put({ ...row.doc, printed: true });
      await db.put({ ...doc, shelcode: code });
    } catch (e) {
      setShelcodeAssignError(`Failed to assign shelcode: ${e}`);
    }
  };
  let rawDoc:(Partial<ItemInfo> & {_rev?: string, _id: string})|null = docInfo.doc;

  const handleSave:React.SubmitEventHandler<HTMLFormElement> = (ev) => {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    const extraDataText = fd.get("_extraData");
    assert(typeof extraDataText === "string");
    const mountTimeDoc = JSON.parse(extraDataText);
    // Merge live doc over mount-time snapshot so out-of-band changes
    // (template_id, tags set via direct db.put) survive a form save.
    const data = {...mountTimeDoc, ...(docInfo.doc ?? {})};
    fd.delete("_extraData");
    data["attrs"] = {};
    for (const [k, v] of fd) {
      if (k.startsWith("attr:")) {
        const subKey = k.replace(/^attr:/, "");
        if (v == null || v === "")
          delete data["attrs"][subKey];
        else
          data["attrs"][subKey] = v;
      } else {
        data[k] = v;
      }
    }
    if ("deleted" in data) {
      delete data["deleted"];
      if (data["deleted_at"] == null) {
        data["deleted_at"] = new Date().toISOString();
      }
    } else {
      data["deleted_at"] = null;
    }
    data["is_template"] = fd.has("is_template");
    if ("qty" in data) {
      const n = parseInt(data["qty"], 10);
      data["qty"] = Number.isFinite(n) && n >= 1 ? n : 1;
    }
    const fieldsets = ev.target.querySelectorAll<HTMLFieldSetElement>("fieldset");
    const setDisabled = (disabled: boolean) => {
      for (const el of fieldsets) {
        el.disabled = disabled;
      }
    };
    setDisabled(true);
    db.put(data).then(() => {
      setNewAttrNames(new Set());
      setDisabled(false);
      const inputEls = ev.target.querySelectorAll<HTMLInputElement|HTMLTextAreaElement>("input, textarea");
      for (const el of inputEls) {
        if (el.type === "hidden")
          continue;
        if (!("defaultvalue" in el.dataset))
          continue;
        el.classList.add("input-unchanged");
      }
    });
  };
  const handleQuickAdd:React.SubmitEventHandler<HTMLFormElement> = (ev) => {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    const name = fd.get("name");
    assert(typeof name === "string");
    db.put({
      _id: `item:${uuid.v7()}`,
      name,
      notes: "",
      in: id,
    });
    const inp = ev.target.querySelector("input[name=name]");
    assert(inp != null && inp instanceof HTMLInputElement);
    inp.value = "";
  };
  const handleAddAttr:React.SubmitEventHandler<HTMLFormElement> = (ev) => {
    ev.preventDefault();
    const formEl:HTMLFormElement = ev.target;
    const fd = new FormData(formEl);
    const name = fd.get("addAttrName");
    assert(typeof name === "string");
    if (!attrNameRegex.test(name))
      return;
    if (name in doc.attrs)
      return;

    let nameInp:HTMLInputElement | null = null;
    for(let controlEl of formEl.elements){
      if (controlEl instanceof HTMLInputElement && controlEl.name == "addAttrName") {
        nameInp = controlEl;
        break;
      }
    }
    assert(nameInp != null);
    nameInp.value = "";

    setNewAttrNames(new Set([name, ...newAttrNames]));

    setFocusAttrName(name);
    return false;
  };

  if (docInfo.state == "loading") {
    return <p>loading</p>;
  }

  const { error } = docInfo;

  if (error != null) {
    if (isPouchError(error, "not_found")) {
      rawDoc = {
        _id: id,
        name: "",
        notes: "",
        ...(searchParams.get("is_template") === "1" ? { is_template: true } : {}),
      };
    } else {
      return <p>Error: <pre>{error.toString()}</pre></p>;
    }
  }

  assert(rawDoc != null);

  const doc = normalizeItemInfo(rawDoc);
  console.log({doc: doc});

  const allAttrNames:string[] = sortUniqueStrings(newAttrNames, Object.keys(doc.attrs));

  const unlinkTemplate = () => {
    const updated = {...doc, template_id: null};
    db.put(updated).catch(console.error);
  };
  const linkTemplate = (templateId: string) => {
    const updated = {...doc, template_id: templateId};
    db.put(updated).catch(console.error);
    setShowTemplatePicker(false);
  };

  return <>
    {doc.in ? <ItemById id={doc.in} /> : null}
    <form id={newAttrFormId} onSubmit={handleAddAttr}></form>
    <form key={`${doc._id}`} id={mainFormId} onSubmit={handleSave}>
      <input type="hidden" name="_extraData" defaultValue={JSON.stringify(doc)} />
      <fieldset style={{display: "flex", flexDirection: "column"}}>
        {doc.is_template && <div style={{fontWeight: "bold", color: "#666", marginBottom: "4px"}}>Template</div>}

        {/* Template link / picker (only for non-templates) */}
        {!doc.is_template && (doc.template_id
          ? <ItemTemplateLink templateId={doc.template_id} onUnlink={unlinkTemplate} />
          : showTemplatePicker
            ? <TemplatePicker onSelect={linkTemplate} onCancel={() => setShowTemplatePicker(false)} />
            : <div style={{marginBottom: "4px"}}>
                <button type="button" onClick={() => setShowTemplatePicker(true)}>Link to template…</button>
              </div>
        )}

        {showMovePicker
          ? <ItemPicker
              label="Move into…"
              excludeId={doc._id}
              onSelect={newParent => {
                const updated = {...doc, in: newParent, wherein: null};
                db.put(updated).catch(console.error);
                setShowMovePicker(false);
              }}
              onCancel={() => setShowMovePicker(false)}
            />
          : <span>
              <InputRow name="in" defaultValue={doc.in} inline={true} />
              <button type="button" onClick={() => setShowMovePicker(true)} style={{marginLeft: "4px"}}>Move…</button>
              {doc.in && <button type="button" onClick={() => db.put({...doc, in: null, wherein: null}).catch(console.error)} style={{marginLeft: "4px"}}>Remove from parent</button>}
            </span>
        }
        <InputRow name="wherein" defaultValue={doc.wherein} label="Where:" />
        <div>{doc._id}</div>
        <InputRow
          name="name"
          defaultValue={doc.name}
          testId="item-name"
          {...(!doc.name && doc.template_id ? {style: {fontStyle: "italic"}} : {})}
        />
        {!doc.is_template && <span style={{display: "flex", gap: "4px", alignItems: "baseline"}}>
          <InputRow key={doc.shelcode || ""} name="shelcode" defaultValue={doc.shelcode} inputRef={shelcodeInputRef} inline={true} />
          <button type="button" onClick={startShelcodeScanner} title="Scan shelcode">📷</button>
          {!doc.shelcode && <button type="button" onClick={assignShelcode} data-testid="assign-shelcode-button" title="Auto-assign next available shelcode">Assign</button>}
        </span>}
        {shelcodeAssignError && <span style={{color: "red", fontSize: "small"}}>{shelcodeAssignError}</span>}
        {!doc.is_template && <InputRow name="qty" label="Qty:" type="number" defaultValue={doc.qty != null ? String(doc.qty) : "1"} />}
        <div>
          <table className="custom-attrs-list" style={{width: "100%"}}>
            <tbody id={attrsTbodyId}>
              {allAttrNames.map(
                (name) => {
                  const ref = (el: HTMLInputElement) => {
                    if (name === focusAttrName) {
                      el.focus();
                      setFocusAttrName(null);
                    }
                    return () => undefined;
                  };
                  const sugg = attrData.valuesByName[name];
                  return <AttrRow name={name} inputRef={ref} defaultValue={doc.attrs[name] || ""} key={name} {...(sugg ? {valueSuggestions: sugg} : {})} />;
                }
              )}
            </tbody>
            {doc.template_id && <ItemTemplateInheritedAttrs templateId={doc.template_id} ownAttrNames={allAttrNames} />}
          </table>
        </div>
        <span>
          <datalist id={attrNameSuggestionsId}>
            {attrData.names.filter(n => !allAttrNames.includes(n)).map(n => <option key={n} value={n} />)}
          </datalist>
          <input type="text" name="addAttrName" form={newAttrFormId} list={attrNameSuggestionsId} />
          <button type="submit" form={newAttrFormId}>Add Attr</button>
        </span>
        <div>
          <label>Tags:</label>
          <TagEditor
            tags={doc.tags ?? []}
            onAdd={tag => db.put({...doc, tags: [...(doc.tags ?? []), tag]}).catch(console.error)}
            onRemove={tag => db.put({...doc, tags: (doc.tags ?? []).filter(t => t !== tag)}).catch(console.error)}
          />
        </div>
        <div>
          <label>Barcodes:</label>
          <ScancodeEditor
            scancodes={doc.scancodes ?? []}
            onAdd={entry => db.put({...doc, scancodes: [...(doc.scancodes ?? []), entry]}).catch(console.error)}
            onRemove={i => db.put({...doc, scancodes: (doc.scancodes ?? []).filter((_, j) => j !== i)}).catch(console.error)}
          />
        </div>
        {doc.template_id && <ItemTemplateNotes templateId={doc.template_id} />}
        <InputRow name="notes" defaultValue={doc.notes} style={{minHeight: "5em"}} textarea={true} />
        <InputRow label="Template:" type="checkbox" name="is_template" defaultChecked={doc.is_template === true} value="yes" />
        <InputRow label="Delete:" type="checkbox" name="deleted" defaultChecked={doc.deleted_at != null} value="yes" />
        <button type="submit">Save</button>
      </fieldset>
    </form>
    {doc.is_template && <ItemTemplateChildren templateId={doc._id} />}
    <ItemAttachments itemId={doc._id} />
    <ItemIconSection doc={doc} />
    {!doc.is_template && <>
      <form onSubmit={handleQuickAdd}>
        <fieldset>
          <input type="text" name="name" />
          <button type="submit">Add New</button>
        </fieldset>
      </form>
      <ItemChildren id={doc._id} />
      <ItemFriends itemId={doc._id} />
    </>}
  </>;
};
